/**
 * The conversation's limit (R39, R43; board O „Limit rozmowy"): from 80% of the fuller window, under
 * the composer, how much is used and when it frees up; once a window is reached, the notice that
 * the plan and the builder carry on without Buddy. Below 80% nothing is drawn (`meterView`).
 */

import clsx from 'clsx';
import { Trans, useTranslation } from 'react-i18next';

import type { MeterView } from '~/entities/ai-conversation';

import { resetWords } from '../lib/limitWording';

import { ClockGlyph } from './ChatGlyphs';
import surfaces from './surfaces.module.css';
import styles from './UsageMeter.module.css';

interface UsageMeterProps {
  readonly meter: MeterView;
  readonly now: Date;
}

export function UsageMeter({ meter, now }: UsageMeterProps) {
  const { t, i18n } = useTranslation();
  const when = resetWords(t, meter.resetsAt, now, i18n.language);
  const label =
    meter.window === 'fiveHour'
      ? t('web:aiPlan.meter.fiveHour', { percent: meter.percent })
      : t('web:aiPlan.meter.weekly', { percent: meter.percent });

  return (
    <div className={styles.meter}>
      <div className={styles.caption}>
        <span>{label}</span>
        {when !== null && <span className={styles.reset}>{t('web:aiPlan.meter.resets', { when })}</span>}
      </div>
      <span
        className={styles.track}
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={meter.percent}
      >
        <span className={clsx(styles.fill, meter.blocked && styles.fillFull)} style={{ width: `${String(meter.percent)}%` }} />
      </span>
      {meter.blocked && (
        <p className={surfaces.notice} role="status">
          <ClockGlyph className={clsx(surfaces.noticeGlyph, styles.warn)} />
          <span>
            <Trans
              i18nKey={when === null ? 'web:aiPlan.meter.blockedLater' : 'web:aiPlan.meter.blocked'}
              values={{ when }}
              components={{ b: <b /> }}
            />
          </span>
        </p>
      )}
    </div>
  );
}
